import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { authApi } from '@/lib/api'
import { useAuthStore } from '@/store/auth'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { toast } from 'sonner'

const loginSchema = z.object({
  email: z.string().email('请输入有效的邮箱地址'),
  code: z.string().length(6, '验证码为6位'),
})

type LoginForm = z.infer<typeof loginSchema>

export const Route = createFileRoute('/login')({
  component: LoginComponent,
})

function LoginComponent() {
  const navigate = useNavigate()
  const { setUser, setToken } = useAuthStore()
  const [isLoading, setIsLoading] = useState(false)
  const [codeSent, setCodeSent] = useState(false)
  const [countdown, setCountdown] = useState(0)

  const form = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      code: '',
    },
  })

  const onSubmit = async (data: LoginForm) => {
    setIsLoading(true)
    try {
      const response = await authApi.login({
        email: data.email,
        code: data.code,
      })

      if (response.success && response.data) {
        setUser(response.data.user)
        // 同时存 token 到 localStorage：移动端浏览器阻止第三方 Cookie 时，
        // 后续请求走 Authorization Bearer header 发送（见 api.ts 请求拦截器）。
        if (response.data.token) setToken(response.data.token)
        if (import.meta.env.DEV) console.debug('[Auth] 登录成功：', { userId: response.data.user?.id })
        toast.success('登录成功')
        navigate({ to: '/projects' })
      } else {
        toast.error(response.error || '登录失败')
      }
    } catch (error: any) {
      toast.error(error.error || '登录失败')
    } finally {
      setIsLoading(false)
    }
  }

  const sendCode = async () => {
    const email = form.getValues('email')
    if (!email) {
      toast.error('请输入邮箱')
      return
    }

    setIsLoading(true)
    try {
      const response = await authApi.sendCode(email)
      if (response.success) {
        toast.success('验证码已发送')
        setCodeSent(true)
        setCountdown(60)
        const timer = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(timer)
              return 0
            }
            return prev - 1
          })
        }, 1000)
        if (response.debugCode) {
          if (import.meta.env.DEV) console.debug('[Auth] 开发环境验证码：', { length: String(response.debugCode).length })
          toast.info(`开发环境验证码: ${response.debugCode}`, { duration: 10000 })
        }
      } else {
        toast.error(response.error || '发送失败')
      }
    } catch (error: any) {
      toast.error(error.error || '发送失败')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold">登录</CardTitle>
          <CardDescription>
            使用 @ptranslation.net 邮箱验证码登录
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>邮箱</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="your@ptranslation.net"
                        autoComplete="email"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="sr-only">验证码</FormLabel>
                    <div className="flex items-center gap-2">
                      <FormControl>
                        <Input
                          placeholder="6位验证码"
                          maxLength={6}
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          {...field}
                        />
                      </FormControl>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={sendCode}
                        disabled={isLoading || countdown > 0}
                        className="shrink-0"
                      >
                        {countdown > 0 ? `${countdown}s` : codeSent ? '重新发送' : '获取验证码'}
                      </Button>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" className="w-full" disabled={isLoading || !codeSent}>
                {isLoading ? '登录中...' : '登录'}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  )
}
